import { Dispatch, SetStateAction, useState, useEffect, useRef } from "react";
import FileTree from "files/components/FileTree";
import { resolveRouterPath } from "files/hooks/useFiles";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";

interface FileSelectShadcnProps {
  selectedFile: FileInfo | null;
  setSelectedFile: Dispatch<SetStateAction<FileInfo | null>>;
  onSelectFile: (file: FileInfo) => void;
  maxHeight?: number;
  placeholder?: string;
  className?: string;
}

/**
 * FileSelectShadcn - A shadcn/ui wrapper for file selection from cloud storage.
 *
 * This component uses shadcn/ui for the collapsible UI and wraps the existing
 * MUI FileTree component for the actual tree navigation.
 *
 * Click-to-select behavior:
 * - User clicks on a file → file is selected → collapsible closes
 * - The "Start Validation" button in the modal serves as the confirmation step
 */
const FileSelectShadcn = ({
  selectedFile,
  setSelectedFile,
  onSelectFile,
  placeholder = "Select source file...",
  className,
}: FileSelectShadcnProps) => {
  const [open, setOpen] = useState(false);
  const prevSelectedFileRef = useRef<FileInfo | null>(null);

  // Auto-select when a file (not directory) is clicked
  useEffect(() => {
    // Only auto-select if:
    // 1. A file is selected (not null)
    // 2. It's actually a file (not a directory)
    // 3. It's different from the previous selection
    // 4. The collapsible is open (user is browsing)
    if (
      selectedFile &&
      selectedFile.type === FileType.FILE &&
      selectedFile !== prevSelectedFileRef.current &&
      open
    ) {
      prevSelectedFileRef.current = selectedFile;
      onSelectFile(selectedFile);
      setOpen(false);
    }
  }, [selectedFile, onSelectFile, open]);

  // Determine display text
  const displayFile = selectedFile?.type === FileType.FILE ? selectedFile : null;
  const displayText = displayFile ? resolveRouterPath(displayFile.key) : placeholder;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn("w-full", className)}>
      <CollapsibleTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-between text-left font-normal h-auto min-h-11 py-2",
            !displayFile && "text-muted-foreground"
          )}
        >
          <span className="truncate flex-1 text-left">{displayText}</span>
          {open ? (
            <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          ) : (
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="rounded-lg border bg-white p-4">
          {/* FileTree (MUI) wrapped in shadcn container */}
          <FileTree
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            showFiles
            maxHeight={200}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default FileSelectShadcn;
